import React, { useEffect, useState } from 'react';
import { Eye, TrendingUp, Wand2, Share2 } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_LABELS = { '/': 'Accueil', '/services': 'Services', '/configurateur': 'Configurateur', '/contact': 'Contact', '/mentions-legales': 'Mentions légales', '/partage': 'Projets partagés' };

const Kpi = ({ icon: Icon, label, value, hint, id }) => (
  <div className="bg-[#1a1a1a] border border-gold/20 p-5" data-testid={id}>
    <div className="flex items-center justify-between mb-3"><p className="text-[10px] uppercase tracking-[0.25em] text-gray-500">{label}</p><Icon className="w-4 h-4 text-gold" /></div>
    <p className="text-3xl font-playfair text-white">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{hint}</p>
  </div>
);

export const VisitsPanel = () => {
  const [s, setS] = useState(undefined);
  useEffect(() => { fetch(`${API_URL}/api/stats/visits`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then(setS).catch(() => setS(null)); }, []);
  if (s === undefined) return <div className="h-32 bg-[#1a1a1a] border border-gold/20 animate-pulse" data-testid="visits-loading" />;
  if (!s) return <p className="text-sm text-gray-500 border border-gold/20 p-5" data-testid="visits-error">Statistiques d'audience momentanément indisponibles.</p>;
  const max = Math.max(1, ...s.pages.map((p) => p.count));
  return (
    <div className="space-y-4" data-testid="visits-panel">
      <h3 className="text-xl font-playfair text-white">Audience du site</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Eye} label="Pages vues aujourd'hui" value={s.today} hint="depuis minuit, hors administrateur" id="kpi-today" />
        <Kpi icon={TrendingUp} label="Pages vues · 7 jours" value={s.last7} hint={`${s.last30} sur 30 jours`} id="kpi-7d" />
        <Kpi icon={Wand2} label="Configurations reçues" value={s.configurations30} hint="sur 30 jours" id="kpi-configs" />
        <Kpi icon={Share2} label="Projets partagés" value={s.shares30} hint="sur 30 jours" id="kpi-shares" />
      </div>
      <div className="grid lg:grid-cols-[2fr_1fr] gap-4">
        <div className="bg-[#1a1a1a] border border-gold/20 p-5 h-64">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-3">Pages vues par jour · 30 jours</p>
          <ResponsiveContainer width="100%" height="85%">
            <AreaChart data={s.daily.map((d) => ({ ...d, label: d.day.slice(8, 10) + '/' + d.day.slice(5, 7) }))}>
              <defs><linearGradient id="vg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#d4b886" stopOpacity={0.6} /><stop offset="1" stopColor="#d4b886" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="label" stroke="#666" fontSize={10} interval={4} padding={{ left: 14, right: 14 }} />
              <Tooltip contentStyle={{ background: '#111', border: '1px solid #a68966' }} labelStyle={{ color: '#d4b886' }} formatter={(v) => [v, 'pages vues']} />
              <Area type="monotone" dataKey="count" stroke="#d4b886" fill="url(#vg)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#1a1a1a] border border-gold/20 p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-4">Pages les plus consultées</p>
          {s.pages.length === 0 && <p className="text-xs text-gray-500">Les premières visites apparaîtront ici.</p>}
          <ul className="space-y-3">
            {s.pages.map((p) => (
              <li key={p.path}>
                <div className="flex justify-between text-xs mb-1"><span className="text-gray-300">{PAGE_LABELS[p.path] || p.path}</span><span className="text-gold">{p.count}</span></div>
                <div className="h-1 bg-white/5"><div className="h-1 bg-gold" style={{ width: `${(p.count / max) * 100}%` }} /></div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
